// PRD 16 C2 — the data-collection ledger. The CLAIMS live here; the screen
// only paints them.
//
// WHY THIS IS A STATIC MODULE AND NOT A FETCH. The obvious build is an
// endpoint that introspects the live schema and returns it. This is better,
// and not only because `backend/api/app.py` is frozen against new routers.
//
// The panel's entire value is being true when a security reviewer reads it
// adversarially. A runtime fetch makes truth a property of one deployment at
// one moment: add a column to `events`, and the panel keeps rendering
// whatever the reviewer's tenant happened to return, or starts describing a
// field nobody wrote copy for. Pinning the claims to the schema FILE with a
// test (`schemaPin.test.ts`) makes truth a build-time invariant instead — a
// schema change that this ledger does not describe fails CI before it can
// ship a panel that lies. The failure mode moves from "the page quietly
// misleads a buyer" to "the build is red", which is the trade we want.
//
// EVERY LINE BELOW MUST BE SUBSTANTIABLE FROM CODE OR A PASSING TEST. One
// false claim here is worse than not shipping the panel at all. The `source`
// field on each row is not decoration: it is where the claim was checked, and
// a claim that cannot name one does not go in. Claims deliberately LEFT OUT
// are recorded at the bottom of this file.
//
// DECLARED IS NOT SENT — the correction that shaped this file (2026-08-16).
// The first draft printed the seven event types as if all seven were
// produced. Three were not: `section_leave`, `term_dwell` and `term_rail_open`
// existed only as enum members. The single producer of analytics events in the
// whole tree is `frontend/src/widget/mount.ts`.
//
// TWO OF THE THREE NOW HAVE PRODUCERS (2026-08-16, later the same day). The
// missing `term_dwell` was a defect, not a design: without it `median_dwell_ms`
// is NULL on every row, so the whole ENGAGEMENT axis was blank on real traffic.
// The widget now measures a hover's duration and sends it
// (`frontend/src/widget/dwell.ts`), which also makes `dwell_ms` a populated
// column.
//
// Dwell is reported as its own number and is never multiplied into a usage
// rank. The `confusion_score` that used to do exactly that —
// `hover_count × median_dwell_ms/1000 + 5 × abandonment_within_5s` — was
// removed the same day: it summed careful study with bouncing off, so one
// number could not be read in either direction. `term_rail_open` is sent when a reader taps a term to open its
// definition panel. `section_leave` is still emitted by nothing, and that is
// now a decision rather than an oversight: the active-section observer tracks
// one section at a time, so a leave is the same instant as the `section_view`
// that follows it and carries no information that event does not already have.
// (PRD 16 §4 asserts "section_view → section_leave are emitted today". The PRD
// was WRONG about the leave event; see `backend/analytics/section_dropoff.py`,
// which found the same thing independently and infers exits from the last
// `section_view`. Do not use the PRD as a source for this panel.)
//
// `block_id` remains the one column nothing fills.
//
// So each row carries a `status`, and the panel prints BOTH layers:
//   sent      — the shipped widget produces this today
//   declared  — the schema or the enum has it; nothing produces it
//
// The declared rows are kept and labelled rather than dropped. A reviewer who
// opens `schema.sql` or `types.py` — which is what an adversarial reader
// does — finds these names, and a panel that had silently omitted them would
// read as concealment of the one thing it exists to be trusted about.
// "Declared, nothing sends it" is both more credible and more useful than
// silence, and it is the ceiling on what could be stored without a code
// change, which is the number a security reviewer actually wants.
//
// The direction of the original error is the "safe" one for privacy — it
// overstated collection. That is not a mitigation. The panel's entire value
// is that an adversarial reader can check every line, and a checkable
// falsehood costs more than the claim was worth.
//
// LANGUAGE RULE, non-negotiable (CLAUDE.md §Non-goals, docs/business/05
// §5.2): say "we don't collect it", NEVER "we anonymize it". A hashed IP is
// pseudonymous, not anonymous — IPv4 is a 4-billion-key space, so the hash
// stays personal data under GDPR Art. 4(5)/Recital 26. Claiming anonymization
// invites a DPIA; claiming non-collection answers a vendor questionnaire in
// one line. `noAnonymize.test.ts` scans this directory for the word.
//
// COUNSEL REVIEW PENDING (docs/business/05 §5.2): the customer-facing wording
// in this file has NOT been reviewed by counsel. It is written as mechanical
// statements of what the code does, with no compliance, certification or
// regulatory assertion anywhere, precisely so that it stays defensible until
// that review happens. Do not add one.

/**
 * Whether a thing is actually produced, or only declared.
 *
 * `sent` — the shipped widget populates this today.
 * `declared` — the schema or the event enum carries it and no code produces
 * it. Kept visible on purpose; see the header.
 */
export type EmissionStatus = "sent" | "declared";

/** One column of the `events` table, described in the customer's language. */
export type CollectedField = {
  /** Must match a column name in `backend/analytics/schema.sql`. */
  column: string;
  type: string;
  status: EmissionStatus;
  description: string;
};

/**
 * Every column the event table has. There is no second event table, and no
 * column on this one that is not listed here — `schemaPin.test.ts` asserts
 * both directions against `backend/analytics/schema.sql`, and asserts each
 * row's `status` against the widget's actual emit call sites.
 *
 * This is therefore the CEILING: the endpoint would store these columns if a
 * client sent them, and the `declared` rows are the ones no shipped code
 * sends.
 */
export const COLLECTED_FIELDS: CollectedField[] = [
  {
    column: "doc_id",
    type: "text",
    status: "sent",
    description: "Which published document the event happened in.",
  },
  {
    column: "org_slug",
    type: "text",
    status: "sent",
    description:
      "Which customer the document belongs to. Resolved server-side from the license key or Origin; whatever the browser sends is overwritten.",
  },
  {
    column: "session_id",
    type: "text",
    status: "sent",
    description:
      "A random UUID minted in sessionStorage for one browser tab. It is not derived from anything about the reader, and nothing links it to another visit.",
  },
  {
    column: "ts",
    type: "timestamp",
    status: "sent",
    description: "The browser's clock when the event happened.",
  },
  {
    column: "ts_received",
    type: "timestamp",
    status: "sent",
    description: "The server's clock when the batch arrived.",
  },
  {
    column: "type",
    type: "text",
    status: "sent",
    description: "Which of the event types below this row is.",
  },
  {
    column: "term_id",
    type: "text",
    status: "sent",
    description:
      "The glossary term involved, as an id local to the document. It identifies a term, not a reader.",
  },
  {
    column: "block_id",
    type: "text",
    status: "declared",
    description:
      "Would name the paragraph a reader was in. No event we emit carries it, so every row is empty.",
  },
  {
    column: "section_id",
    type: "text",
    status: "sent",
    description: "The section of the document the reader was in.",
  },
  {
    column: "dwell_ms",
    type: "integer",
    status: "sent",
    description:
      "How many milliseconds a glossary definition stayed open, on the term_dwell row that closes a hover. It is a duration, not a timestamp, and it says nothing about who was reading.",
  },
  {
    column: "ua_class",
    type: "text",
    status: "sent",
    description:
      "One of desktop, mobile, tablet or other — computed in the browser. The User-Agent string itself is never sent.",
  },
  {
    column: "date",
    type: "date",
    status: "sent",
    description:
      "Derived from ts by the database. The browser does not send it.",
  },
];

export type DeclaredEvent = {
  name: string;
  status: EmissionStatus;
  description: string;
};

/**
 * Every event type the system declares. Six of the seven are produced;
 * `section_leave` exists only as an enum member with no producer anywhere in
 * the tree. `schemaPin.test.ts` pins the names against `EventType` in
 * `backend/analytics/types.py` AND each `status` against the emit call sites
 * in `frontend/src/widget/mount.ts`.
 */
export const EVENT_TYPES: DeclaredEvent[] = [
  {
    name: "session_start",
    status: "sent",
    description: "A reader opened the document.",
  },
  {
    name: "session_end",
    status: "sent",
    description: "The tab was closed or hidden.",
  },
  {
    name: "section_view",
    status: "sent",
    description: "A section scrolled into view.",
  },
  {
    name: "section_leave",
    status: "declared",
    description:
      "Would mark a section scrolling out of view. Nothing emits it, so it has never been recorded — the section a reader moves to is already reported by section_view, which makes a separate leave event redundant.",
  },
  {
    name: "term_hover",
    status: "sent",
    description: "A glossary term was hovered, with the term's id.",
  },
  {
    name: "term_dwell",
    status: "sent",
    description:
      "The hover ended, carrying how long the definition was open in milliseconds. One of these closes each term_hover.",
  },
  {
    name: "term_rail_open",
    status: "sent",
    description:
      "A reader tapped a term to open its definition panel — the deliberate version of a hover, with the term's id.",
  },
];

export type Claim = { claim: string; source: string };

/**
 * Stated as non-collection, never as anonymization. Each row names where it
 * was verified.
 */
export const NOT_COLLECTED: Claim[] = [
  {
    claim:
      "No IP address, and no hash of one. There is no column for it, and the ingest path never reads a network address into a row.",
    source:
      "backend/analytics/ingest.py — backend/tests/analytics/test_no_pii_in_events.py posts an X-Forwarded-For header and asserts its value is absent from the stored row.",
  },
  {
    claim:
      "No User-Agent string. The four-way desktop/mobile/tablet/other class is computed in the browser and is the only thing sent.",
    source:
      "frontend/src/widget/analytics.ts::uaClass — the same test asserts the posted User-Agent is absent from the row.",
  },
  {
    claim:
      "No Referer, and no URL of the page the reader came from or is on.",
    source:
      "The same test posts a Referer and asserts it is absent from the row.",
  },
  {
    claim:
      "No cookies. The widget sets none and writes nothing to localStorage; the event endpoint identifies the customer from the license key or the Origin header, never from a cookie.",
    source:
      "frontend/src/widget/analytics.ts — sessionStorage is the only storage it touches. backend/api/routes/analytics.py::_resolve reads no cookie.",
  },
  {
    claim:
      "No reader name, email, or account identifier of any kind — including a hashed or pseudonymous one. There is no column that could hold one.",
    source:
      "backend/analytics/schema.sql — the table above is the whole record.",
  },
  {
    claim:
      "No page text, keystrokes, form input, mouse coordinates, or scroll positions.",
    source:
      "The event types above are the complete declared set, six of them are produced at all, and no event carries a payload beyond the columns above. A hover duration is a number of milliseconds; nothing records where on the page a cursor was.",
  },
  {
    claim:
      "No identity that survives a visit. session_id lives in sessionStorage, so it ends with the browser tab and the same reader returning tomorrow is a new, unrelated id.",
    source: "frontend/src/widget/analytics.ts::genSessionId.",
  },
];

/**
 * The consequences of the row above, stated as properties rather than gaps.
 * These are permanent by design (PRD 16 §8) and must not be presented as a
 * roadmap item.
 */
export const CONSEQUENCES: Claim[] = [
  {
    claim:
      "Session counts are counts of VISITS, not of people. One reader who opens your docs on Monday and again on Tuesday is two sessions, and nothing in the system can tell that they were the same person.",
    source:
      "unique_sessions counts distinct session_id, which is minted per tab.",
  },
  {
    claim:
      "Cross-visit cohorts, per-reader retention, and per-person journeys cannot be produced from this data — not by us, and not by anyone we could hand the database to.",
    source:
      "There is no identifier that spans two visits. This is structural, not a setting.",
  },
  {
    claim:
      "The dashboards read only daily aggregates, never the raw event rows.",
    source:
      "backend/tests/analytics/test_dashboard_no_raw_events.py replaces the events table with a view that errors on any read, and asserts the dashboard endpoints still return 200.",
  },
  {
    claim:
      "One aggregate records that two terms were opened in the same visit, and roughly how far apart. It records that as daily counts: how many visits opened both, and a typical gap between the two. It does not record which visit, in what order, or anything that links one visit to another.",
    source:
      "backend/analytics/schema.sql::term_pair_metrics is keyed (org_slug, doc_id, term_a, term_b, date) and holds counts plus one median gap. There is no session column: pair_rollup.py counts DISTINCT session_id inside the aggregate and keeps nothing else about a visit. Order cannot survive it either — the pair is keyed term_a < term_b and the gap is ABS(), so the smaller term id is always first regardless of which was opened first.",
  },
  {
    claim:
      "A pair that only a handful of visits opened is left out of that report entirely, rather than shown with a blank rate — a pair opened by two or three visits comes close to describing one person's reading.",
    source:
      "backend/analytics/term_coupling.py::build_coupling drops any pair under DEFAULT_MIN_PAIR_SESSIONS before building a row, so nothing about it reaches the response; only a count of how many were left out does. backend/tests/analytics/test_term_coupling.py::test_route_puts_no_sub_threshold_pair_on_the_wire asserts the term never appears in the API body.",
  },
  {
    claim:
      "One aggregate records whether a term was quicker to read the second time within the same visit. It records that as daily counts and two typical durations per term. It does not record which visit, how many times that visit opened the term, when it did, or the order it opened any other term in.",
    source:
      "backend/analytics/schema.sql::term_learning_metrics is keyed (org_slug, doc_id, term_id, date). There is no session column, no ordinal and no timestamp: learning_rollup.py reduces each visit to a first and a later figure inside the aggregate and keeps nothing else about it. The only order it holds is first-versus-later on a single term, which cannot reconstruct the order terms were opened in.",
  },
  {
    claim:
      "On any day too few visits contributed to, those two durations are not calculated at all — not hidden from the report, not stored and withheld. Only the counts are kept for that day.",
    source:
      "backend/analytics/learning_rollup.py writes first_dwell_ms and later_dwell_ms as NULL below MIN_REPEAT_SESSIONS_STORED visits, because a first-and-later pair off one visit is that visit's reading rather than a pattern. backend/tests/analytics/test_learning_curve.py::test_rollup_withholds_the_durations_on_a_thin_day asserts the columns are empty in the database itself, not merely absent from the response.",
  },
  {
    claim:
      "Nothing outside the aggregator reads the event table at all.",
    source:
      "Every query against events lives in backend/analytics/; schemaPin.test.ts fails the build if one appears anywhere else.",
  },
];

/**
 * Where document text goes, and what a customer can do about it. This is the
 * honest disclosure a reviewer asks for second, right after "what do you
 * collect".
 */
export const PROCESSING: Claim[] = [
  {
    claim:
      "Document text is sent to the configured extraction model when a document is ingested. That is the one point at which your content leaves the deployment, and it happens at ingest, never while a reader is reading.",
    source:
      "run_extraction has exactly three callers — the extract, /ingest/quick and /try endpoints, all of them ingest. Nothing on the path that serves a published document to a reader calls a model.",
  },
  {
    claim:
      "The extraction model is configurable, including to an endpoint you host. MAISIGHT_LLM_PROVIDER selects anthropic (the default) or openai-compat, and openai-compat plus MAISIGHT_LLM_BASE_URL reaches any OpenAI-compatible server, self-hosted included.",
    source: "backend/extract/llm_client.py::get_llm_client.",
  },
  {
    claim:
      "The browser extension can run extraction on your own key. The model call is made by the extension itself and only the resulting terms are posted to the backend, which never receives the key.",
    source:
      "frontend/src/extension/byokExtract.ts and backend/api/routes/ingest_quick.py::_materialize_byok_proposal.",
  },
  {
    claim:
      "Reader events have exactly one destination: POST /analytics/events on the mAiSight API, written to a local analytics database. No third-party analytics service is in the path.",
    source: "frontend/src/widget/analytics.ts posts to that one endpoint.",
  },
];

// CLAIMS DELIBERATELY LEFT OUT — each was considered and could not be
// substantiated from code or a passing test, so it is absent rather than
// softened. Re-adding one means building the thing first.
//
//  - RETENTION / DELETION. Nothing deletes rows from `events`. The 90-day
//    `_RETENTION` constant in `backend/analytics/aggregator.py:24` bounds the
//    window the aggregator REBUILDS at boot; it is not a purge. A "we keep
//    events for 90 days" line is the single most likely claim to be read as
//    load-bearing by a reviewer and is not true today.
//  - ANY COMPLIANCE, CERTIFICATION, OR REGULATORY STATEMENT. No GDPR, SOC 2,
//    DPA, HIPAA, residency, or "compliant with" language, here or on the
//    screen. Those are counsel's to write, not ours (docs/business/05 §5.2).
//  - ENCRYPTION AND HOSTING CLAIMS. Properties of a deployment, not of this
//    code, and this panel is rendered from the code.
